import React, { useEffect, useState } from "react";
import {
  Card,
  Table,
  Button,
  Spinner,
  Alert,
  Container,
  Breadcrumb,
} from "react-bootstrap";
import { Link } from "react-router-dom";
import { FiDownload, FiRefreshCw, FiBarChart2 } from "react-icons/fi";
import { toast } from "react-toastify";
import { format, startOfWeek, startOfMonth, startOfYear } from "date-fns";
import DatePicker from "react-datepicker";
import Reportselect from "./Reportselect";
import Pagination from "../Constant/Pagination";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";
import GlobalSearchableDropdown from "../Constant/SearchableDropdown";
import { getVendorWiseReport } from "../../services/ReportServices";
import { searchcategoryOptionApi } from "../../services/SearchService";

const VendorWiseReport = () => {
const periodOptions = [
  { label: "All", value: "all" },
  { label: "Yesterday", value: "yesterday" },
  { label: "Last 7 Days", value: "7_days" },
  { label: "Last 15 Days", value: "15_days" },
  { label: "Last 30 Days", value: "30_days" },
  { label: "Last Month", value: "last_month" },
  { label: "Last 3 Months", value: "3_months" },
  { label: "Custom", value: "custom" },
];


  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(0);

  // Category and Subcategory state
  const [dropdownCategory, setDropdownCategory] = useState(null);
  const [dropdownSubCategory, setDropdownSubCategory] = useState(null);
  const [dropdownCategoryOption, setDropdownCategoryOption] = useState([]);
  const [dropdownSubCategoryOption, setDropdownSubCategoryOption] = useState(
    []
  );
  const [filteredSubcategoryOptions, setFilteredSubCategoryOptions] = useState(
    []
  );

 const [selectedPeriod, setSelectedPeriod] = useState({
  label: "Last 30 Days",
  value: "30_days",
});


  const [param, setParam] = useState({
    page: 1,
    page_size: 10,
    query: "",
    category_id: "",
    sub_category_id: "",
    period: "30_days",
    start_date: "",
    end_date: "",
  });

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return format(new Date(dateString), "dd MMM yyyy");
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) return "-";
    return `₹${value.toLocaleString("en-IN")}`;
  };

  // Load categories and subcategories
  useEffect(() => {
    searchcategoryOptionApi()
      .then((res) => {
        const categoryOpts =
          res?.data?.results?.map((item) => ({
            label: item.name,
            value: item.id,
            subcategories: item.subcategories || [],
          })) || [];

        setDropdownCategoryOption(categoryOpts);

        const allSubCats = categoryOpts.flatMap((cat) =>
          cat.subcategories.map((sub) => ({
            label: sub.name,
            value: sub.id,
            categoryId: cat.value,
          }))
        );

        setDropdownSubCategoryOption(allSubCats);
        setFilteredSubCategoryOptions(allSubCats);
      })
      .catch(() => toast.error("Failed to load categories"));
  }, []);

  // Initialize dates for weekly period
  useEffect(() => {
  const today = new Date();
  let start_date = "";
  let end_date = "";

  switch (selectedPeriod?.value) {
    case "all":
      start_date = "";
      end_date = "";
      break;

    case "yesterday":
      const yesterday = new Date(today);
      yesterday.setDate(today.getDate() - 1);
      start_date = format(yesterday, "yyyy-MM-dd");
      end_date = format(yesterday, "yyyy-MM-dd");
      break;

    case "7_days":
      const last7Days = new Date(today);
      last7Days.setDate(today.getDate() - 6); // today counts as 1
      start_date = format(last7Days, "yyyy-MM-dd");
      end_date = format(today, "yyyy-MM-dd");
      break;

    case "15_days":
      const last15Days = new Date(today);
      last15Days.setDate(today.getDate() - 14);
      start_date = format(last15Days, "yyyy-MM-dd");
      end_date = format(today, "yyyy-MM-dd");
      break;

    case "30_days":
      const last30Days = new Date(today);
      last30Days.setDate(today.getDate() - 29);
      start_date = format(last30Days, "yyyy-MM-dd");
      end_date = format(today, "yyyy-MM-dd");
      break;

    case "last_month":
      const firstDayOfCurrentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      const lastDayOfLastMonth = new Date(firstDayOfCurrentMonth - 1);
      start_date = format(new Date(lastDayOfLastMonth.getFullYear(), lastDayOfLastMonth.getMonth(), 1), "yyyy-MM-dd");
      end_date = format(lastDayOfLastMonth, "yyyy-MM-dd");
      break;

    case "3_months":
      const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 2, 1);
      start_date = format(threeMonthsAgo, "yyyy-MM-dd");
      end_date = format(today, "yyyy-MM-dd");
      break;

    case "custom":
      start_date = param.start_date;
      end_date = param.end_date;
      break;

    default:
      break;
  }

  setParam((prev) => ({
    ...prev,
    period: selectedPeriod?.value || "all",
    start_date,
    end_date,
    page: 1,
  }));
}, [selectedPeriod]);


  // Fetch data when params change
  useEffect(() => {
    const delay = setTimeout(() => fetchData(), 300);
    return () => clearTimeout(delay);
  }, [param]);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      if (param.period === "custom") {
        if (!param.start_date || !param.end_date) {
          throw new Error(
            "Please select both start and end dates for custom period"
          );
        }

        const start = new Date(param.start_date);
        const end = new Date(param.end_date);

        if (start > end) {
          throw new Error("Start date cannot be after end date");
        }

        const maxDateRange = 365 * 5; // 5 years
        const diffInDays = Math.floor((end - start) / (1000 * 60 * 60 * 24));

        if (diffInDays > maxDateRange) {
          throw new Error("Date range cannot exceed 5 years");
        }
      }

      const response = await getVendorWiseReport({
        ...param,
        export: false,
      });

      if (!response.data) {
        throw new Error("Invalid response format from server");
      }

      setReportData(response.data.results || []);
      setTotalCount(response.data.count || response.data.results?.length || 0);
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.message ||
        "Failed to fetch vendor report";

      toast.error(errorMessage);
      setError(errorMessage);
      setReportData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryChange = (selected) => {
    setDropdownCategory(selected);
    setDropdownSubCategory(null);
    const filtered = selected
      ? dropdownSubCategoryOption.filter(
          (sub) => sub.categoryId === selected.value
        )
      : dropdownSubCategoryOption;

    setFilteredSubCategoryOptions(filtered);
    setParam((prev) => ({
      ...prev,
      category_id: selected?.value || "",
      sub_category_id: "",
      page: 1,
    }));
  };

  const handleSubCategoryChange = (selected) => {
    setDropdownSubCategory(selected);
    const relatedCat = dropdownCategoryOption.find(
      (cat) => cat.value === selected?.categoryId
    );
    if (
      relatedCat &&
      (!dropdownCategory || relatedCat.value !== dropdownCategory.value)
    ) {
      setDropdownCategory(relatedCat);
      setFilteredSubCategoryOptions(
        dropdownSubCategoryOption.filter(
          (sub) => sub.categoryId === relatedCat.value
        )
      );
    }

    setParam((prev) => ({
      ...prev,
      category_id: relatedCat?.value || "",
      sub_category_id: selected?.value || "",
      page: 1,
    }));
  };

  const handleSearchChange = (value) => {
    setParam((prev) => ({
      ...prev,
      query: value,
      page: 1,
    }));
  };

  const handlePageChange = (selectedPage) => {
    setParam((prev) => ({
      ...prev,
      page: selectedPage.selected + 1,
    }));
  };

  const handleExport = async () => {
    try {
      setExportLoading(true);
      const response = await getVendorWiseReport({ ...param, export: true });

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `vendor_report_${new Date().toISOString().slice(0, 10)}.xlsx`
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export started successfully");
    } catch (error) {
      toast.error(`Export failed: ${error.message}`);
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Container fluid>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <nav>
          <h2 className="text-primary">Vendor Report</h2>
          <ol className="breadcrumb breadcrumb-arrow mb-0">
            <li className="breadcrumb-item">
              <Link to="/">Dashboard</Link>
            </li>
            <li className="breadcrumb-item active" aria-current="page">
              Vendor Report
            </li>
          </ol>
        </nav>

        <div
          className="d-flex align-items-center gap-2 ms-auto mt-2 mt-md-0"
          style={{ zIndex: 10 }}
        >
          <div style={{ width: "200px" }}>
            <Reportselect />
          </div>
          <Button
            variant="success"
            onClick={handleExport}
            disabled={exportLoading || loading}
            style={{ height: "41px" }}
          >
            {exportLoading ? (
              <Spinner animation="border" size="sm" className="me-1" />
            ) : (
              <FiDownload className="me-1" />
            )}
            Export
          </Button>
        </div>
      </div>

      <Card style={{ minHeight: "350px" }}>
        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h5 className="mb-0">
            <FiBarChart2 className="me-2" /> Vendor-Wise Asset Report
          </h5>

          <div className="d-flex gap-2 flex-wrap align-items-center">
            <DebounceSmallSearchInput
              value={param.query}
              handleSearchChange={handleSearchChange}
            />

            <GlobalSearchableDropdown
              options={dropdownCategoryOption}
              selected={dropdownCategory}
              onSelect={handleCategoryChange}
              placeholder="Category"
              width="180px"
            />

            <GlobalSearchableDropdown
              options={filteredSubcategoryOptions}
              selected={dropdownSubCategory}
              onSelect={handleSubCategoryChange}
              placeholder="Sub-Category"
              width="180px"
            />

            <GlobalSearchableDropdown
              options={periodOptions}
              selected={selectedPeriod}
              onSelect={(sel) =>
                sel
                  ? setSelectedPeriod(sel)
                  : setSelectedPeriod({ label: "All", value: "all" })
              }
              isClearable={true}
              placeholder="Period"
              width="150px"
            />

            {selectedPeriod?.value === "custom" && (
              <div style={{ width: "260px" }}>
                <DatePicker
                  selectsRange
                  startDate={
                    param.start_date ? new Date(param.start_date) : null
                  }
                  endDate={param.end_date ? new Date(param.end_date) : null}
                  onChange={([start, end]) => {
                    setParam((prev) => ({
                      ...prev,
                      period: "custom",
                      start_date: start ? format(start, "yyyy-MM-dd") : "",
                      end_date: end ? format(end, "yyyy-MM-dd") : "",
                      page: 1,
                    }));
                  }}
                  isClearable
                  placeholderText="Select date range"
                  className="form-control form-control-sm"
                  dateFormat="yyyy-MM-dd"
                  minDate={new Date(2000, 0, 1)}
                  maxDate={new Date()}
                />
              </div>
            )}

            
          </div>
        </Card.Header>

        <Card.Body className="p-0">
          {error && (
            <Alert variant="danger">
              {error}
              <Button variant="link" size="sm" onClick={fetchData}>
                <FiRefreshCw /> Try again
              </Button>
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" />
              <p className="mt-2">Loading report data...</p>
            </div>
          ) : (
            <>
              <div className="table-responsive">
                <Table
                  size="sm"
                  className="align-middle text-center text-nowrap medium rounded"
                >
                  <thead className="table-light">
                    <tr>
                      <th>Vendor</th>
                      <th>GRN</th>
                      <th>Received Date</th>
                      <th>Received By</th>
                      <th>Invoice</th>
                      <th>Location</th>
                      <th>Asset Number</th>
                      <th>Asset Name</th>
                      <th>Category</th>
                      <th>Subcategory</th>
                      <th>Cost</th>
                      <th>Purchase Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reportData.length === 0 ? (
                      <tr>
                        <td colSpan="12" className="text-center py-4">
                          <small>No assets found matching your criteria</small>
                        </td>
                      </tr>
                    ) : (
                      reportData.map((item, idx) => (
                        <tr key={idx}>
                          <td>{item.vendor || "-"}</td>
                          <td>{item.grn_number || "-"}</td>
                          <td>{formatDate(item.received_date)}</td>
                          <td>{item.received_by || "-"}</td>
                          <td>{item.invoice_number || "-"}</td>
                          <td>{item.delivery_location || "-"}</td>
                          <td>{item.asset_number || "-"}</td>
                          <td>{item.asset_name || "-"}</td>
                          <td>{item.category || "-"}</td>
                          <td>{item.subcategory || "-"}</td>
                          <td>{formatCurrency(item.cost)}</td>
                          <td>{formatDate(item.purchase_date)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>

              <Pagination
                currentPage={param.page}
                recordsPage={param.page_size}
                dataLength={totalCount}
                handlePagination={handlePageChange}
                showPageInfo
                disabled={loading}
              />
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default VendorWiseReport;
